/**
 * ProjectPartnersRow — Large Logo Cards with Hybrid Logo Handling
 *
 * Design: 150×190 cards distributed across the panel.
 *   - Uploaded logos: subtle tinted bed + object-fit: contain
 *   - No logo: initials on a deterministic gradient background
 *   - Role subtitle under partner name (Sponsor, Partner, etc.)
 *   - Edit mode: inline name editing (manual partners only),
 *     role dropdown, and click-to-replace logo
 *
 * Resolves display data using the dual-source pattern:
 *   - If a partner has `entityGuid`, resolves name/logo from the
 *     community entity map.
 *   - Otherwise falls back to `manualName` / `manualLogoUrl`.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiProjectPartners } from '../../api/useApiProjectPartners';
import { compressImage } from '../../lib/compressImage';
import { uploadFile } from '../../api/fileStorage';
import type { ProjectPartnerResponse } from '../../api/projectPartners';
import { resolveEffectiveLogoUrl } from '../../api/communityMetadata';
import styles from './ProjectPartners.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ProjectPartnersRowProps {
  projectGuid: string;
  /** Whether the current user can edit partners (owner/lead/admin). */
  canEdit: boolean;
  /** Page-level edit mode (controlled by CoreTabPanel toggle). */
  isEditing?: boolean;
  /** Community entities map (seed-shape, keyed by uppercased entity_guid). */
  entityMap: Map<string, Record<string, any>>;
  /**
   * DEV-992. Community entities (array form) for the cascade walker in
   * `resolveEffectiveLogoUrl`. Same data as the entityMap; both are passed
   * because the lookup uses the Map and the cascade walker uses the array.
   */
  communityEntities?: Array<Record<string, any>>;
  /** DEV-992. Community-entity relationships for the cascade walker. */
  communityRelationships?: Array<Record<string, any>>;
  /** Called when user clicks "Add Partner" — parent shows modal/picker. */
  onAddPartner?: () => void;
  /** Called when user clicks "Link L4E" on a manual partner — parent shows entity picker. */
  onLinkEntity?: (partnerId: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Resolved partner shape                                             */
/* ------------------------------------------------------------------ */

interface ResolvedPartner {
  id: string;
  name: string;
  logoUrl: string | null;
  linkUrl: string | null;
  /** Raw manualWebsiteUrl from the API (for editing). */
  websiteUrl: string | null;
  role: string | null;
  initials: string;
  gradientIndex: number;
  /** True when the partner is linked to a community entity (name not editable). */
  isEntityLinked: boolean;
}

/* ------------------------------------------------------------------ */
/*  Role options (match i18n keys)                                     */
/* ------------------------------------------------------------------ */

const ROLE_OPTIONS = [
  'sponsor',
  'partner',
  'collaborator',
  'funder',
  'supporter',
  'advisor',
] as const;

/* ------------------------------------------------------------------ */
/*  Gradient palette for initials fallback                             */
/* ------------------------------------------------------------------ */

const INITIALS_GRADIENTS = [
  'linear-gradient(135deg, #3b82f6, #6366f1)',  // blue → indigo
  'linear-gradient(135deg, #10b981, #059669)',  // emerald → green
  'linear-gradient(135deg, #f59e0b, #d97706)',  // amber → yellow
  'linear-gradient(135deg, #ef4444, #dc2626)',  // red
  'linear-gradient(135deg, #8b5cf6, #7c3aed)',  // violet → purple
  'linear-gradient(135deg, #06b6d4, #0891b2)',  // cyan → teal
  'linear-gradient(135deg, #ec4899, #db2777)',  // pink → fuchsia
  'linear-gradient(135deg, #f97316, #ea580c)',  // orange
];

/** Deterministic gradient from partner name so colors are stable. */
function hashToIndex(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // 32-bit int
  }
  return Math.abs(hash) % INITIALS_GRADIENTS.length;
}

/* ------------------------------------------------------------------ */
/*  Resolve partner display data                                       */
/* ------------------------------------------------------------------ */

function resolvePartner(
  p: ProjectPartnerResponse,
  entityMap: Map<string, Record<string, any>>,
  communityEntities: Array<Record<string, any>>,
  communityRelationships: Array<Record<string, any>>,
): ResolvedPartner {
  let name = p.manualName || '';
  let logoUrl = p.manualLogoUrl || null;
  let linkUrl = p.externalProfileUrl || p.manualWebsiteUrl || null;
  const isEntityLinked = !!p.entityGuid;

  if (p.entityGuid) {
    const entity = entityMap.get(p.entityGuid.toUpperCase());
    if (entity) {
      name = entity.name;
      // DEV-992 — Resolve the partner's logo via the effective-logo cascade
      // (this entity's own logo_url, else the nearest ancestor's). Hampton
      // Rotary as a partner inherits Rotary International's logo when it
      // hasn't set its own. The previous fallback to hero_image_url was
      // wrong — the hero photo is the project's banner image, not a logo.
      const cascaded = resolveEffectiveLogoUrl(
        p.entityGuid,
        communityEntities,
        communityRelationships,
      );
      logoUrl = cascaded || logoUrl;
      if (!p.externalProfileUrl) {
        linkUrl = `/communities/${entity.entity_guid}`;
      }
    }
  }

  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase();

  return {
    id: p.id,
    name,
    logoUrl,
    linkUrl,
    websiteUrl: p.manualWebsiteUrl || null,
    role: p.partnerRole || 'partner',
    initials,
    gradientIndex: hashToIndex(name),
    isEntityLinked,
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ProjectPartnersRow({
  projectGuid,
  canEdit,
  isEditing: isEditingProp,
  entityMap,
  communityEntities,
  communityRelationships,
  onAddPartner,
  onLinkEntity,
}: ProjectPartnersRowProps) {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const { partners, loading, removePartner, editPartner } = useApiProjectPartners(projectGuid);

  // Use page-level editing state from parent
  const editing = !!(isEditingProp && canEdit);

  // Track pending inline edits: partnerId → { name?, role?, url?, entityGuid? }
  const pendingEdits = useRef<Map<string, { name?: string; role?: string; url?: string; entityGuid?: string | null }>>(new Map());

  // Local logo overrides (show new logo immediately before API round-trip)
  const [logoOverrides, setLogoOverrides] = useState<Map<string, string>>(new Map());

  const resolved = useMemo(
    () => partners.map((p) => resolvePartner(
      p,
      entityMap,
      communityEntities || [],
      communityRelationships || [],
    )),
    [partners, entityMap, communityEntities, communityRelationships]
  );

  const handleRemove = useCallback(
    async (partnerId: string) => {
      if (!window.confirm(t('projectDetail.about.removePartnerConfirm'))) return;
      try {
        await removePartner(partnerId);
      } catch (err) {
        console.error('[ProjectPartners] remove failed', err);
      }
    },
    [removePartner, t]
  );

  // Flush all pending edits to the API
  const flushEdits = useCallback(async () => {
    const edits = pendingEdits.current;
    const promises: Promise<void>[] = [];
    for (const [partnerId, changes] of edits.entries()) {
      const body: Record<string, any> = {};
      if (changes.name !== undefined) body.manualName = changes.name;
      if (changes.role !== undefined) body.partnerRole = changes.role;
      if (changes.url !== undefined) body.manualWebsiteUrl = changes.url;
      if (changes.entityGuid !== undefined) body.entityGuid = changes.entityGuid;
      if (Object.keys(body).length > 0) {
        promises.push(editPartner(partnerId, body));
      }
    }
    if (promises.length > 0) {
      try {
        await Promise.all(promises);
      } catch (err) {
        console.error('[ProjectPartners] edit failed', err);
      }
    }
    edits.clear();
  }, [editPartner]);

  // Auto-flush pending edits when page-level edit mode is toggled off
  const prevEditingRef = useRef(false);
  useEffect(() => {
    if (prevEditingRef.current && !editing) {
      flushEdits();
      setLogoOverrides(new Map());
    }
    prevEditingRef.current = editing;
  }, [editing, flushEdits]);

  const trackEdit = useCallback((partnerId: string, field: 'name' | 'role' | 'url' | 'entityGuid', value: string | null) => {
    const edits = pendingEdits.current;
    const existing = edits.get(partnerId) || {};
    (existing as any)[field] = value;
    edits.set(partnerId, existing);
  }, []);

  // Handle logo file selection: compress, upload, save immediately
  const handleLogoChange = useCallback(
    async (partnerId: string, file: File) => {
      try {
        // Show preview immediately
        const reader = new FileReader();
        reader.onload = () => {
          setLogoOverrides((prev) => {
            const next = new Map(prev);
            next.set(partnerId, reader.result as string);
            return next;
          });
        };
        reader.readAsDataURL(file);

        // Compress and upload
        const compressed = await compressImage(file, {
          maxDimension: 400,
          quality: 0.85,
          maxBytes: 200_000,
        });
        const ext = compressed.blob.type === 'image/png' ? 'png' : 'jpg';
        const result = await uploadFile(
          compressed.blob,
          'logos',
          projectGuid,
          `partner-logo-${Date.now()}.${ext}`
        );

        // Persist to API
        await editPartner(partnerId, { manualLogoUrl: result.url });
      } catch (err) {
        console.error('[ProjectPartners] logo upload failed', err);
      }
    },
    [projectGuid, editPartner]
  );

  // ── Responsive: calculate how many cards fit ──
  const PARTNER_CARD_WIDTH = 186; // min-width + padding + gap
  const PARTNER_GAP = 16;
  const rowRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState(999);

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const calculate = () => {
      const w = el.clientWidth;
      const count = Math.max(1, Math.floor((w + PARTNER_GAP) / (PARTNER_CARD_WIDTH + PARTNER_GAP)));
      setMaxVisible(count);
    };
    calculate();
    const ro = new ResizeObserver(() => calculate());
    ro.observe(el);
    return () => ro.disconnect();
  }, [resolved.length]);

  // Nothing to show if loading, or no partners and not in edit mode
  if (loading) return null;
  if (partners.length === 0 && !editing) return null;

  const visiblePartners = editing ? resolved : resolved.slice(0, maxVisible);
  const hasOverflow = !editing && resolved.length > maxVisible;

  return (
    <div className={styles.wrapper} ref={rowRef}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t('projectDetail.about.partnersTitle')}
        </h3>
      </div>

      {/* ── Card row — shows what fits, center-justified ── */}
      {partners.length === 0 ? (
        <div className={styles.empty}>
          {t('projectDetail.about.noPartners')}
        </div>
      ) : (
        <div className={styles.scrollRow}>
          {visiblePartners.map((rp) => {
            const isLink = !editing && rp.linkUrl;
            // DEV-994 — Internal URLs ("/communities/...") must use
            // react-router navigate() to avoid a full page reload, which
            // re-validates auth and (for sysadmins) dumps the user at the
            // 2FA prompt. External (http/https) links still open in a new
            // tab via a standard anchor. Manual-partner profile links may
            // be either. The underlying auth UX for non-sysadmins is
            // tracked separately in DEV-993 (trusted-device cookie).
            const isInternalLink = !!isLink && rp.linkUrl!.startsWith('/');
            const isExternalLink = !!isLink && rp.linkUrl!.startsWith('http');
            const Tag: 'a' | 'div' = isExternalLink ? 'a' : 'div';
            const linkProps: Record<string, any> = {};
            if (isExternalLink) {
              linkProps.href = rp.linkUrl!;
              linkProps.target = '_blank';
              linkProps.rel = 'noopener noreferrer';
            } else if (isInternalLink) {
              linkProps.role = 'link';
              linkProps.tabIndex = 0;
              linkProps.style = { cursor: 'pointer' };
              linkProps.onClick = (e: React.MouseEvent) => {
                e.preventDefault();
                navigate(rp.linkUrl!);
              };
              linkProps.onKeyDown = (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(rp.linkUrl!);
                }
              };
            }

            const displayLogo = logoOverrides.get(rp.id) || rp.logoUrl;

            return (
              <Tag
                key={rp.id}
                className={`${styles.card} ${editing ? styles.cardEditing : ''}`}
                {...(linkProps as any)}
              >
                {/* Remove badge (edit mode) */}
                {editing && (
                  <button
                    className={styles.removeBtn}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(rp.id);
                    }}
                    title={t('projectDetail.about.removePartner')}
                  >
                    ×
                  </button>
                )}

                {/* Logo / initials — left side */}
                {editing ? (
                  <label
                    className={styles.logoEditWrap}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {displayLogo ? (
                      <div className={styles.logoBed}>
                        <img
                          className={styles.logo}
                          src={displayLogo}
                          alt={rp.name}
                        />
                      </div>
                    ) : (
                      <div
                        className={styles.initialsCircle}
                        style={{ background: INITIALS_GRADIENTS[rp.gradientIndex] }}
                      >
                        {rp.initials}
                      </div>
                    )}
                    <div className={styles.logoEditOverlay}>
                      <span className={styles.logoEditIcon}>&#9998;</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleLogoChange(rp.id, file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                ) : displayLogo ? (
                  <div className={styles.logoBed}>
                    <img
                      className={styles.logo}
                      src={displayLogo}
                      alt={rp.name}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div
                    className={styles.initialsCircle}
                    style={{ background: INITIALS_GRADIENTS[rp.gradientIndex] }}
                  >
                    {rp.initials}
                  </div>
                )}

                {/* Name + Role — right side, stacked */}
                <div className={styles.cardInfo}>
                  {editing && !rp.isEntityLinked ? (
                    <input
                      className={styles.editNameInput}
                      defaultValue={rp.name}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => trackEdit(rp.id, 'name', e.target.value)}
                      placeholder={t('projectDetail.about.manualName')}
                    />
                  ) : (
                    <span className={styles.cardName}>{rp.name}</span>
                  )}

                  {editing ? (
                    <select
                      className={styles.editRoleSelect}
                      value={pendingEdits.current.get(rp.id)?.role ?? rp.role ?? 'partner'}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        trackEdit(rp.id, 'role', e.target.value);
                        // Force re-render so controlled value updates
                        setLogoOverrides((prev) => new Map(prev));
                      }}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {t(`projectDetail.about.partnerRoles.${r}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    rp.role && (
                      <span className={styles.cardRole}>
                        {t(`projectDetail.about.partnerRoles.${rp.role}`, { defaultValue: rp.role })}
                      </span>
                    )
                  )}

                  {/* URL + Link L4E (edit mode only) */}
                  {editing && (
                    <>
                      <input
                        className={styles.editUrlInput}
                        defaultValue={rp.websiteUrl || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => trackEdit(rp.id, 'url', e.target.value)}
                        placeholder={t('projectDetail.about.websitePlaceholder')}
                      />
                      {rp.isEntityLinked ? (
                        <button
                          className={styles.linkBtn}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (window.confirm(t('projectDetail.about.unlinkConfirm'))) {
                              trackEdit(rp.id, 'entityGuid', null);
                              // Also set manualName so the card still has a name after unlinking
                              trackEdit(rp.id, 'name', rp.name);
                            }
                          }}
                        >
                          {t('projectDetail.about.unlinkEntity')}
                        </button>
                      ) : (
                        <button
                          className={styles.linkBtn}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onLinkEntity?.(rp.id);
                          }}
                        >
                          {t('projectDetail.about.linkEntity')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </Tag>
            );
          })}

          {/* Add partner card (visible in edit mode) */}
          {canEdit && editing && (
            <button
              className={styles.addCard}
              onClick={onAddPartner}
              title={t('projectDetail.about.addPartner')}
            >
              <span className={styles.addIcon}>+</span>
              <span>{t('projectDetail.about.addPartner')}</span>
            </button>
          )}
        </div>
      )}

      {/* "See all" button — appears when partners overflow */}
      {hasOverflow && (
        <button
          type="button"
          className={styles.viewAllBtn}
          onClick={() => setMaxVisible(resolved.length)}
        >
          {t('projectDetail.about.seeAllPartners', { count: resolved.length })}
        </button>
      )}

      {/* If empty but can edit, show standalone add button */}
      {partners.length === 0 && canEdit && (
        <button
          className={styles.addCard}
          onClick={onAddPartner}
          title={t('projectDetail.about.addPartner')}
          style={{ marginBlockStart: 4 }}
        >
          <span className={styles.addIcon}>+</span>
          <span>{t('projectDetail.about.addPartner')}</span>
        </button>
      )}
    </div>
  );
}
